import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import type { CaseSummary } from '../types/caseApi';

export interface CaseStatusOption {
    value: string;
    label: string;
    color: string;
    barColor: string;
}

export interface CaseStatusDropdownProps {
    caseItem: CaseSummary;
    statusOptions: CaseStatusOption[];
    isOpen: boolean;
    isUpdating: boolean;
    onToggle: (caseId: string) => void;
    onSelect: (caseId: string, newStatus: string) => void;
}

/**
 * The case list's status pill and its working / finalized / archived menu.
 *
 * Deliberately NOT disabled for a ProDowngrade-locked (Pro data, non-Pro team) case: a status is
 * case-list metadata, and /update-status writes only the status, so the server allows it on a
 * locked case (docs/AUTOSAVE_DETAILS.md section 20). The case contents stay read-only. A team
 * that is billing read-only is refused by the server (403 TEAM_BILLING_READ_ONLY) and the app-level
 * BillingReadOnlyGate explains it. The only local block is a status change already in flight.
 */
export const CaseStatusDropdown: React.FC<CaseStatusDropdownProps> = ({
    caseItem,
    statusOptions,
    isOpen,
    isUpdating,
    onToggle,
    onSelect,
}) => {
    const theme = useTheme();
    const currentValue = caseItem.stat || 'working';
    const current = statusOptions.find(opt => opt.value === currentValue);

    return (
        <div style={{ position: 'relative' }} data-status-dropdown>
            {/* Current status pill */}
            <div
                data-testid="case-status-pill"
                aria-disabled={isUpdating}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    backgroundColor: currentValue === 'working' || currentValue === 'finalized' ? 'white' : (current ? current.color + '20' : theme.colors.surface),
                    border: `1px solid ${current?.color || theme.colors.border.default}`,
                    cursor: isUpdating ? 'not-allowed' : 'pointer',
                    opacity: isUpdating ? 0.6 : 1,
                    transition: `all ${theme.transitions.normal}`,
                    position: 'relative',
                    minWidth: '80px',
                    justifyContent: 'space-between',
                }}
                onClick={() => {
                    if (!isUpdating && caseItem.cid) {
                        onToggle(caseItem.cid);
                    }
                }}
            >
                {/* Colored vertical bar on the left */}
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: current?.barColor || theme.colors.border.default,
                    borderRadius: '2px 0 0 2px',
                }} />

                {/* Status text */}
                <span style={{
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: current?.color || theme.colors.text.primary,
                    marginLeft: '8px',
                }}>
                    {current?.label || 'Working'}
                </span>

                {/* Dropdown arrow */}
                <span style={{
                    fontSize: '10px',
                    color: theme.colors.text.secondary,
                    marginLeft: '8px',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: `transform ${theme.transitions.normal}`,
                }}>
                    ▼
                </span>
            </div>

            {/* Dropdown menu */}
            {isOpen && (
                <div
                    data-testid="case-status-menu"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.sm,
                        boxShadow: theme.shadows.md,
                        zIndex: 1000,
                        marginTop: '4px',
                    }}
                >
                    {statusOptions
                        .filter(option => option.value !== currentValue)
                        .map((statusOption) => (
                            <div
                                key={statusOption.value}
                                data-status-option={statusOption.value}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    position: 'relative',
                                    transition: `background-color ${theme.transitions.normal}`,
                                }}
                                onClick={() => {
                                    if (statusOption.value !== caseItem.stat && caseItem.cid) {
                                        onSelect(caseItem.cid, statusOption.value);
                                    }
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = statusOption.color + '10';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                {/* Colored vertical bar on the left */}
                                <div style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '4px',
                                    backgroundColor: statusOption.barColor,
                                    borderRadius: '2px 0 0 2px',
                                }} />

                                {/* Status text */}
                                <span style={{
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.text.primary,
                                    marginLeft: '8px',
                                }}>
                                    {statusOption.label}
                                </span>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};
